import { forwardRef, useImperativeHandle, useRef, useState, type KeyboardEvent } from 'react';
import type { MentionCandidate, MentionPerson } from './types';
import { cn } from './utils';

/** "@" then the partial name being typed, right before the caret. No spaces yet. */
const TRIGGER = /(^|\s)@([^\s@]*)$/;

export interface MentionTextareaHandle {
	focus: () => void;
}

interface MentionTextareaProps {
	value: string;
	onChange: (value: string) => void;
	candidates: MentionCandidate[];
	/** Called whenever someone is picked, so the caller can convert `@Name` on send. */
	onMention: (person: MentionPerson) => void;
	/** Cmd/Ctrl+Enter. */
	onSubmit?: () => void;
	placeholder?: string;
	disabled?: boolean;
	rows?: number;
	className?: string;
}

/**
 * A textarea that offers the thread's people when "@" is typed. Picking one
 * inserts `@Name ` — the readable form; the caller turns it into the token on
 * send (see `toBodyText`). The list opens above the field, since the composer
 * sits at the foot of its panel.
 */
export const MentionTextarea = forwardRef<MentionTextareaHandle, MentionTextareaProps>(
	function MentionTextarea(
		{
			value,
			onChange,
			candidates,
			onMention,
			onSubmit,
			placeholder,
			disabled,
			rows = 3,
			className,
		},
		ref
	) {
		const areaRef = useRef<HTMLTextAreaElement>(null);
		const [query, setQuery] = useState<string | null>(null);
		const [active, setActive] = useState(0);

		useImperativeHandle(ref, () => ({ focus: () => areaRef.current?.focus() }));

		const matches =
			query === null
				? []
				: candidates
						.filter((c) => c.name.toLowerCase().includes(query.toLowerCase()))
						.slice(0, 8);

		const readTrigger = (text: string, caret: number) => {
			const m = TRIGGER.exec(text.slice(0, caret));
			setQuery(m ? m[2] : null);
			setActive(0);
		};

		const pick = (person: MentionCandidate) => {
			const el = areaRef.current;
			if (!el) return;
			const caret = el.selectionStart ?? value.length;
			const before = value
				.slice(0, caret)
				.replace(TRIGGER, (_m, lead: string) => `${lead}@${person.name} `);
			const next = before + value.slice(caret);
			onChange(next);
			onMention({ userId: person.userId, name: person.name });
			setQuery(null);
			requestAnimationFrame(() => {
				el.focus();
				el.setSelectionRange(before.length, before.length);
			});
		};

		const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
			if (matches.length > 0) {
				if (e.key === 'ArrowDown') {
					e.preventDefault();
					setActive((i) => (i + 1) % matches.length);
					return;
				}
				if (e.key === 'ArrowUp') {
					e.preventDefault();
					setActive((i) => (i - 1 + matches.length) % matches.length);
					return;
				}
				if (e.key === 'Enter' || e.key === 'Tab') {
					e.preventDefault();
					pick(matches[active]);
					return;
				}
				if (e.key === 'Escape') {
					e.preventDefault();
					setQuery(null);
					return;
				}
			}
			if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && onSubmit) {
				e.preventDefault();
				onSubmit();
			}
		};

		return (
			<div className="relative">
				{matches.length > 0 && (
					<div
						role="listbox"
						className="absolute bottom-full left-0 z-20 mb-1 w-72 max-w-full overflow-hidden rounded-[var(--r-3)] border border-line-2 bg-popover p-1 shadow-lg"
					>
						{matches.map((c, i) => (
							<button
								key={c.userId}
								type="button"
								role="option"
								aria-selected={i === active}
								// mousedown, not click: keep focus in the textarea.
								onMouseDown={(e) => {
									e.preventDefault();
									pick(c);
								}}
								onMouseEnter={() => setActive(i)}
								className={cn(
									'flex w-full cursor-pointer flex-col items-start rounded-md px-2.5 py-1.5 text-left',
									i === active ? 'bg-surface-fill' : 'bg-transparent'
								)}
							>
								<span className="text-sm text-text-1">{c.name}</span>
								{c.subtitle && (
									<span className="text-xs text-text-4">{c.subtitle}</span>
								)}
							</button>
						))}
					</div>
				)}
				<textarea
					ref={areaRef}
					value={value}
					onChange={(e) => {
						onChange(e.target.value);
						readTrigger(
							e.target.value,
							e.target.selectionStart ?? e.target.value.length
						);
					}}
					onKeyDown={handleKeyDown}
					onClick={(e) =>
						readTrigger(e.currentTarget.value, e.currentTarget.selectionStart ?? 0)
					}
					onBlur={() => setQuery(null)}
					placeholder={placeholder}
					disabled={disabled}
					rows={rows}
					className={cn(
						'w-full resize-none rounded-[var(--r-3)] border border-line-2 bg-surface-input px-3 py-2 text-base text-text-1 outline-none placeholder:text-text-4 focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40 disabled:opacity-60',
						className
					)}
				/>
			</div>
		);
	}
);
